/**
 * The outbox drain loop.
 *
 * Specification: ../test/drain-loop.spec.ts.
 *
 * `Outbox.drain()` takes ONE batch per call by design — the caller owns the
 * cadence, because a self-looping drain would burn an event's whole attempt
 * budget inside a single call and turn one transient hiccup into a quarantine.
 * This is that caller, and it is one copy rather than two.
 *
 * It was two: `apps/pos/src/main.ts` and `apps/kds/src/main.ts`, twelve lines
 * each, and they had diverged. The till's copy had been repaired after an
 * incident and carried the reasoning in comments; the kitchen screen's copy was
 * the version from before the repair. Both bugs below were found by cutting the
 * network in a real browser and watching a counter refuse to move — neither was
 * visible to any test, because a loop assembled from `window.setTimeout` inside
 * a module that also mounts a Vue app cannot be driven by one.
 *
 * Hence every dependency is a parameter. An app passes `window`'s timers; the
 * suite passes a clock it controls.
 */

/**
 * The one field of `Outbox.DrainResult` the cadence depends on.
 *
 * Structural and deliberately narrow: the loop decides when to try again, and
 * nothing else about a batch is its business. `transportError` is `string | null`
 * on the real result — the message, kept for the banner — so this admits a
 * string and reads it for TRUTH rather than comparing it to `true`. Typing it
 * `boolean` here compiled and would have made every failed batch look clean.
 */
export interface DrainOutcome {
  readonly transportError: string | null | boolean;
}

export interface DrainLoopDeps {
  /** One batch. Usually `() => outbox.drain(upload)`. */
  readonly drain: () => Promise<DrainOutcome>;
  /** Re-read whatever the screen shows about the queue. Runs after every attempt. */
  readonly afterDrain?: () => void | Promise<void>;
  readonly isOnline: () => boolean;
  readonly setTimeout: (fn: () => void, ms: number) => number;
  readonly clearTimeout: (id: number) => void;
  /** The delay after a clean round trip, and the floor. */
  readonly baseDelayMs?: number;
  /**
   * The ceiling on the retry delay.
   *
   * THIRTY SECONDS FOR EVERY SURFACE. The two copies said 60 000 and 30 000 with
   * no reason given for either, which is how a number becomes folklore.
   *
   * This cap governs one case only: the device believes it is online and the
   * server does not answer — the API is down, or a captive portal is eating the
   * request. The other case, the network itself returning, is handled by
   * `reconnected()` and does not wait for a timer at all.
   *
   * So the cost of a shorter cap is one HTTP request per thirty seconds per
   * device while a server is down: nothing, for a venue with four devices. What
   * it buys is half the time a till spends not knowing whether a guest's money
   * reached anybody. That trade is not close.
   */
  readonly maxDelayMs?: number;
}

export interface DrainLoop {
  /** Drain now, then keep draining. Calling it twice is not two loops. */
  start(): void;
  /**
   * The network came back — cancel whatever was pending and go now.
   *
   * NOT the same as resetting the delay. The kitchen screen's `online` handler
   * set its backoff to two seconds and called nothing: the sixteen seconds
   * already on the clock went on ticking, because resetting a variable does not
   * touch a timer that has already been scheduled. A cook bumped a ticket
   * offline, the wifi came back, and the server did not hear about it for
   * another sixteen seconds. Food gets cooked twice that way.
   */
  reconnected(): void;
  stop(): void;
}

const DEFAULT_BASE_MS = 2_000;
const DEFAULT_MAX_MS = 30_000;

export function createDrainLoop(deps: DrainLoopDeps): DrainLoop {
  const base = deps.baseDelayMs ?? DEFAULT_BASE_MS;
  const max = deps.maxDelayMs ?? DEFAULT_MAX_MS;

  let delayMs = base;
  let timer: number | null = null;
  let running = false;
  let stopped = true;
  /** A reconnect arrived while a drain was in flight. Go again when it lands. */
  let again = false;

  const schedule = (ms: number): void => {
    if (stopped) return;
    if (timer !== null) deps.clearTimeout(timer);
    timer = deps.setTimeout(() => void attempt(), ms);
  };

  async function attempt(): Promise<void> {
    timer = null;
    if (stopped) return;

    /**
     * OFFLINE RESCHEDULES. IT DOES NOT RETURN.
     *
     * The missing `else` here was the till's original bug: the loop stopped dead
     * the moment a venue lost its wifi, permanently, and coming back online did
     * not restart it because there was no longer a timer for the `online`
     * handler to adjust. The outbox sat undrained until somebody reloaded the
     * page, with the banner cheerfully reading "Připojeno".
     */
    if (!deps.isOnline()) {
      schedule(delayMs);
      return;
    }

    /**
     * ONE AT A TIME.
     *
     * Two concurrent drains are not a performance problem, they are a double
     * send: the outbox is a blocking FIFO and its ordering is what stops a void
     * being applied before the line it voids. The obvious fix for `reconnected`
     * — clear the timer and call this function — produces exactly that whenever
     * a reconnect lands mid-request, which is the likeliest moment for one to
     * land. So a reconnect during a drain is remembered rather than obeyed.
     */
    if (running) {
      again = true;
      return;
    }
    running = true;

    let failed: boolean;
    try {
      // Truthiness, because the outbox reports the MESSAGE and not a flag.
      failed = Boolean((await deps.drain()).transportError);
    } catch {
      /**
       * A REJECTION IS A TRANSPORT FAILURE, not the end of the loop.
       *
       * `drain()` reports the ordinary failure in its result, but the fetch
       * underneath can still reject outright — DNS, an aborted request, a
       * service worker that declined. An unhandled rejection here kills the loop
       * silently, which is the same outage as the one above and with no banner
       * to show for it.
       */
      failed = true;
    } finally {
      running = false;
    }

    try {
      await deps.afterDrain?.();
    } catch {
      // A screen that failed to re-render is not a reason to stop draining.
    }

    // Exponential while the server is unreachable, reset on one clean round
    // trip — a device that has been cut off for an hour must not then take an
    // hour to notice it is back.
    delayMs = failed ? Math.min(delayMs * 2, max) : base;

    if (again) {
      again = false;
      delayMs = base;
      schedule(0);
      return;
    }
    schedule(delayMs);
  }

  return {
    start(): void {
      if (!stopped) return;
      stopped = false;
      delayMs = base;
      schedule(0);
    },
    reconnected(): void {
      if (stopped) return;
      delayMs = base;
      /**
       * UNCONDITIONALLY, and the in-flight case is `attempt`'s to handle.
       *
       * This used to test `running` here as well, and that second copy of the
       * check made the one in `attempt` UNREACHABLE — deleting the real guard
       * broke nothing and every test still passed. A defence with no path to it
       * is not a defence; it is a comment that compiles. One owner now, and the
       * concurrency test reaches it.
       */
      schedule(0);
    },
    stop(): void {
      stopped = true;
      again = false;
      if (timer !== null) deps.clearTimeout(timer);
      timer = null;
    },
  };
}
